import {
	type BoundActions,
	createHook,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type {
	AlphaExtendedThinkingMode,
	AutoActionApprovalMode,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { getAppFilters } from './getAppFilters';
import { getDerivedDefaultChatMode } from './getDerivedDefaultChatMode';
import { getDerivedReasoningMode } from './getDerivedReasoningMode';
import { store } from './store';
import type {
	RovoApp,
	OrgKnowledge,
	ReasoningMode,
	SourceMode,
	ChatMode,
	ChatModeState,
	WebSearchDefaultOptions,
} from './types';

const selector = (state: ChatModeState, key: string) => {
	const chatMode = state[key] || getDerivedDefaultChatMode();
	return {
		...chatMode,
		...getDerivedReasoningMode(chatMode.reasoningMode),
		appFilters: getAppFilters(chatMode.orgKnowledge),
	};
};

export const useChatModeStore: HookFunction<
	{
		appFilters: RovoApp[] | undefined;
		selectedCloudIds: string[];
		appFiltersSetByPrompt: boolean;
		deepResearchEnabled: boolean;
		thinkDeeperEnabled: boolean;
		fastModeEnabled: boolean;
		autoReasoningEnabled: boolean;
		alphaExtendedThinkingMode: AlphaExtendedThinkingMode | undefined;
		autoActionApprovalMode: AutoActionApprovalMode;
		taskModeEnabled: boolean;
		reasoningMode: ReasoningMode;
		webSearchEnabled: boolean;
		sourceMode: SourceMode;
		orgKnowledge: OrgKnowledge;
		autoApplyEnabled: boolean;
		useCurrentPageContext: boolean;
	},
	BoundActions<
		ChatModeState,
		{
			setAutoActionApprovalMode: (
				key: string,
				autoActionApprovalMode: AutoActionApprovalMode,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setReasoningMode: (
				key: string,
				reasoningMode: ReasoningMode,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			resetReasoningMode: (key: string) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setDeepResearchEnabled: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setWebSearchEnabled: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			seedWebSearchDefault: (
				key: string,
				webSearchDefaultEnabled: boolean,
			) => ({ dispatch, getState }: StoreActionApi<ChatModeState>) => boolean;
			setSourceMode: (
				key: string,
				sourceMode: SourceMode,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setOrgKnowledge: (
				key: string,
				orgKnowledge: OrgKnowledge,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setAppFiltersFromPrompt: (
				key: string,
				filteredApps: RovoApp[],
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setUseCurrentPageContext: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setSelectedCloudIds: (
				key: string,
				selectedCloudIds: string[],
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setChatMode: (
				key: string,
				chatMode: Partial<
					Omit<
						ChatMode,
						'orgKnowledge' | 'deepResearchEnabled' | 'thinkDeeperEnabled' | 'fastModeEnabled'
					>
				>,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			getChatMode: (key: string) => ({ getState }: StoreActionApi<ChatModeState>) => {
				appFilters: RovoApp[] | undefined;
				selectedCloudIds: string[];
				appFiltersSetByPrompt: boolean;
				deepResearchEnabled: boolean;
				thinkDeeperEnabled: boolean;
				fastModeEnabled: boolean;
				autoReasoningEnabled: boolean;
				alphaExtendedThinkingMode: AlphaExtendedThinkingMode | undefined;
				autoActionApprovalMode: AutoActionApprovalMode;
				taskModeEnabled: boolean;
				reasoningMode: ReasoningMode;
				webSearchEnabled: boolean;
				sourceMode: SourceMode;
				orgKnowledge: OrgKnowledge;
				autoApplyEnabled: boolean;
				useCurrentPageContext: boolean;
			};
			clearMode: (
				key: string,
				options?: WebSearchDefaultOptions,
			) => ({ dispatch, getState }: StoreActionApi<ChatModeState>) => void;
		}
	>,
	string
> = createHook(store, {
	selector,
});
